import { CENTS_PER_KM_CAP_KM, CENTS_PER_KM_RATES, calculateCentsPerKm } from "@stb/jurisdiction-au"

import {
  ATO_BUSINESS_KM_URL,
  capKm,
  displayYear,
  formatCheckedOn,
  HOME_FINANCIAL_YEAR,
  rate,
} from "../components/home/home-content"
import { CentsPerKmCalculator, formatAud, SimpleTable, SourceLine } from "../components/landing"
import { CentsScene } from "../components/landing/product-art"
import type { Guide } from "./guide-types"

/** Served at /cents-per-km-calculator (T398), not under /guides. Copy source: docs/design/landing/copy.md section 2.10 and the web-landing-seo-article design. */
const fy = displayYear(HOME_FINANCIAL_YEAR)
const startYear = Number(HOME_FINANCIAL_YEAR.slice(0, 4))
const checked = formatCheckedOn(rate.checkedOn)

const maxClaim = (year: string) => {
  const r = calculateCentsPerKm(year, CENTS_PER_KM_CAP_KM)
  return r.ok ? formatAud(r.deduction) : ""
}

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

/** Newest two years, as in the design; older years live on the history page. */
const recent = CENTS_PER_KM_RATES.toReversed().slice(0, 2)

const EXAMPLE_KM = 3200
const example = calculateCentsPerKm(HOME_FINANCIAL_YEAR, EXAMPLE_KM)

export const rateGuide: Guide = {
  slug: "cents-per-km-calculator",
  title: `ATO cents per km rate ${fy}: ${rate.cents} cents`,
  description: `The ATO cents per kilometre rate is ${rate.cents} cents for ${fy}, up to ${capKm} business km per car. Work out your deduction with our free estimate.`,
  crumb: "Cents per km rate",
  h1: `ATO cents per kilometre rate ${fy}: ${rate.cents} cents`,
  published: "2026-10-27",
  reviewed: "2026-10-27",
  badge: "Free tool",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO, cents per kilometre method (individuals)", href: rate.sourceUrl },
    { label: "business page", href: ATO_BUSINESS_KM_URL },
  ],
  answer: (
    <>
      For the {fy} income year (1 July {startYear} to 30 June {startYear + 1}) the cents per
      kilometre rate for cars is {rate.cents} cents per kilometre. You can claim up to {capKm}{" "}
      business kilometres per car. The rate covers fuel, registration, insurance, repairs and
      decline in value.
    </>
  ),
  figure: example.ok ? (
    <figure className="bg-card border-border grid gap-2 rounded-xl border p-4">
      <CentsScene
        km={EXAMPLE_KM}
        capKm={CENTS_PER_KM_CAP_KM}
        rate={`${rate.cents} c`}
        year={`FY ${fy}`}
        result={formatAud(example.deduction)}
      />
      <figcaption className="text-muted-foreground text-center text-sm">
        Example: {EXAMPLE_KM.toLocaleString("en-AU")} business km × {rate.cents} c/km in FY {fy} is{" "}
        {formatAud(example.deduction)} (0 to {capKm} km cap).
      </figcaption>
    </figure>
  ) : undefined,
  related: [
    { label: "Mileage tracker: automatic GPS logbook", href: "/mileage-tracker" },
    { label: "Logbook method: when it pays off", href: "/logbook-method" },
    { label: "ATO logbook requirements: what to record", href: "/guides/ato-logbook-requirements" },
  ],
  toc: [
    { id: "calculator", label: "Work out your deduction" },
    { id: "rates", label: "Rates by year" },
    { id: "which-method", label: "Cents per km or logbook?" },
    { id: "faq", label: "Common questions" },
  ],
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          Work out your deduction
        </h2>
        <CentsPerKmCalculator />
      </section>

      <section aria-labelledby="rates" className="grid gap-3">
        <h2 id="rates" className="text-2xl font-semibold">
          Rates by year
        </h2>
        <SimpleTable
          caption="Rates by year"
          columns={["Income year", "Rate", `Maximum claim at ${capKm} km`]}
          rows={[
            ...recent.map((r) => ({
              header: displayYear(r.financialYear),
              cells: [`${r.cents} c/km`, maxClaim(r.financialYear)],
            })),
            {
              header: "Earlier years",
              cells: [
                "See the archive",
                <a key="history" href="/guides/ato-cents-per-km-rate-history" className={linkClass}>
                  Rate history
                </a>,
              ],
            },
          ]}
        />
        <SourceLine
          sources={[{ label: "ATO", href: rate.sourceUrl }]}
          financialYear={recent.map((r) => displayYear(r.financialYear)).join(" and ")}
          checked={checked}
        />
        <p className="text-muted-foreground text-sm">
          The maximum claim is our arithmetic (rate × {capKm} km), not a figure the ATO publishes.
        </p>
      </section>

      <section aria-labelledby="which-method" className="grid gap-3">
        <h2 id="which-method" className="text-2xl font-semibold">
          Cents per km or logbook?
        </h2>
        <p className="max-w-[70ch]">
          Use cents per km when you drive few business kilometres and want a simple claim. Use the
          logbook method when your business driving or running costs are high. With cents per km you
          can claim at most {capKm} km per car. If you drive more for business, the logbook method
          may give a bigger deduction. It needs a 12-week logbook and records of your actual costs.
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "Do I need receipts for the cents per km method?",
      answer: `You do not need receipts for car costs under this method, but you must be able to show how you worked out your business kilometres. Source: ATO, cents per kilometre method, FY ${fy}, checked ${checked}.`,
    },
    {
      question: "Who sets the rate?",
      answer:
        "The ATO sets the cents per kilometre rate in a legislative instrument; it does not change every year. Check the ATO page for the current figure before you claim.",
    },
  ],
}
